import React, { useState, useEffect } from 'react';

const TOUR_KEY = 'casefile_tour_completed';

const STEPS = [
  {
    title: '👋 Welcome to CaseFile Creator',
    body: 'This quick tour will walk you through the key features. You can skip at any time and restart from your Profile page.',
    target: null,
  },
  {
    title: '📊 Dashboard',
    body: 'Your Dashboard shows live case stats, recent activity, and storage usage. Everything important is surfaced here at a glance.',
    target: '/',
  },
  {
    title: '📁 Cases',
    body: 'Create and manage cases here. Each case has a full lifecycle — from Open through Investigation, Pending Review, and Closed — with an enforced workflow.',
    target: '/cases',
  },
  {
    title: '🔍 Evidence',
    body: 'Log evidence items with photos, descriptions, and chain-of-custody tracking. Use the Transfer button to record custody changes.',
    target: '/evidence',
  },
  {
    title: '📄 Documents',
    body: 'Upload and manage case documents. Version history is preserved — upload a new version without losing the old one.',
    target: '/documents',
  },
  {
    title: '⚖️ DA Requests',
    body: 'District Attorneys submit case file requests here. You can approve, reject, link a casefile, and communicate via the built-in comment thread.',
    target: '/requests',
  },
  {
    title: '👥 Personnel',
    body: 'Invite officers, assign departments, manage roles and permissions. Use permission templates (Investigator, Supervisor, etc.) for quick setup.',
    target: '/personnel',
  },
  {
    title: '📈 Analytics',
    body: 'View case clearance rates, evidence stats, and activity trends. Export reports or schedule them to be emailed automatically.',
    target: '/analytics',
  },
  {
    title: '🔐 Audit Log',
    body: 'Every action in the system is logged here — who did what, when, and from which IP. Fully CJIS-compliant audit trail.',
    target: '/timeline',
  },
  {
    title: '✅ You\'re all set!',
    body: 'That\'s the full tour. You can restart it anytime from your Profile page. If you need help, contact your agency administrator.',
    target: null,
  },
];

const OnboardingTour = () => {
  const [visible, setVisible] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    const done = localStorage.getItem(TOUR_KEY);
    if (!done) {
      // Small delay so the app finishes rendering first
      const t = setTimeout(() => setVisible(true), 1200);
      return () => clearTimeout(t);
    }
  }, []);

  const handleNext = () => {
    if (step < STEPS.length - 1) {
      setStep(s => s + 1);
    } else {
      handleFinish();
    }
  };

  const handlePrev = () => {
    if (step > 0) setStep(s => s - 1);
  };

  const handleFinish = () => {
    localStorage.setItem(TOUR_KEY, 'true');
    setVisible(false);
  };

  const handleSkip = () => {
    localStorage.setItem(TOUR_KEY, 'true');
    setVisible(false);
  };

  if (!visible) return null;

  const current = STEPS[step];
  const progress = ((step + 1) / STEPS.length) * 100;

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)',
      zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center',
      padding: '20px'
    }}>
      <div style={{
        background: '#1e293b', borderRadius: '16px', padding: '32px',
        width: '100%', maxWidth: '480px', border: '1px solid #334155',
        boxShadow: '0 20px 60px rgba(0,0,0,0.6)'
      }}>
        {/* Progress bar */}
        <div style={{ marginBottom: '24px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '6px' }}>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Step {step + 1} of {STEPS.length}</span>
            <button onClick={handleSkip} style={{
              background: 'none', border: 'none', color: '#64748b',
              cursor: 'pointer', fontSize: '0.75rem', padding: 0
            }}>Skip tour</button>
          </div>
          <div style={{ height: '4px', background: '#0f172a', borderRadius: '2px', overflow: 'hidden' }}>
            <div style={{
              height: '100%', width: `${progress}%`,
              background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
              borderRadius: '2px', transition: 'width 0.3s ease'
            }} />
          </div>
        </div>

        {/* Content */}
        <h2 style={{ color: '#f1f5f9', fontSize: '1.25rem', marginBottom: '12px', fontWeight: 700 }}>
          {current.title}
        </h2>
        <p style={{ color: '#94a3b8', lineHeight: 1.6, marginBottom: '28px', fontSize: '0.95rem' }}>
          {current.body}
        </p>

        {/* Navigation */}
        <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', alignItems: 'center' }}>
          <button
            onClick={handlePrev}
            disabled={step === 0}
            style={{
              padding: '10px 20px', borderRadius: '8px', border: '1px solid #334155',
              background: 'transparent', color: step === 0 ? '#475569' : '#e2e8f0',
              cursor: step === 0 ? 'not-allowed' : 'pointer', fontWeight: 600, fontSize: '0.9rem'
            }}
          >
            ← Back
          </button>

          {/* Step dots */}
          <div style={{ display: 'flex', gap: '6px' }}>
            {STEPS.map((_, i) => (
              <div key={i} onClick={() => setStep(i)} style={{
                width: i === step ? '20px' : '8px', height: '8px',
                borderRadius: '4px', cursor: 'pointer', transition: 'all 0.2s',
                background: i === step ? '#3b82f6' : i < step ? '#1d4ed8' : '#334155'
              }} />
            ))}
          </div>

          <button
            onClick={handleNext}
            style={{
              padding: '10px 24px', borderRadius: '8px', border: 'none',
              background: step === STEPS.length - 1 ? '#10b981' : '#3b82f6',
              color: '#fff', cursor: 'pointer', fontWeight: 700, fontSize: '0.9rem'
            }}
          >
            {step === STEPS.length - 1 ? '✓ Done' : 'Next →'}
          </button>
        </div>
      </div>
    </div>
  );
};

// Export a function to restart the tour (used from Profile page)
export const restartTour = () => {
  localStorage.removeItem(TOUR_KEY);
  window.location.reload();
};

export default OnboardingTour;
